import {
  Button,
  Chip,
  Dialog,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { ApiRequest } from "GlobalFunctions";

/*=========================================================
BUDGET HISTORY - Trazabilidad de asignaciones

Muestra todas las asignaciones de dinero disponible del usuario
(la activa y las anteriores) y permite ver los pedidos que
consumieron cada asignación.
=========================================================*/
export default function BudgetHistory(props: any) {
  const { open, setOpen, user } = props;

  const [list, setList] = useState<any[]>([]);
  const [movements, setMovements] = useState<any[]>([]);
  const [selectedBudgetId, setSelectedBudgetId] = useState<number | null>(null);

  useEffect(() => {
    if (open && user?.user_id) {
      loadHistory();
      setMovements([]);
      setSelectedBudgetId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user?.user_id]);

  const loadHistory = async () => {
    await ApiRequest({
      url: "/purchases/budgets/history",
      method: "get",
      headers: {
        ltkn: localStorage.getItem("ltkn"),
      },
      query: { user_id: user?.user_id },
      setResponse: (response: any) => {
        setList(response?.list || []);
      },
    });
  };

  const loadMovements = async (budget_id: number) => {
    setSelectedBudgetId(budget_id);
    await ApiRequest({
      url: "/purchases/budgets/movements",
      method: "get",
      headers: {
        ltkn: localStorage.getItem("ltkn"),
      },
      query: { budget_id },
      setResponse: (response: any) => {
        setMovements(response?.list || []);
      },
    });
  };

  const currency = (value: any) =>
    `$${Number(value || 0).toLocaleString("es-CO", { maximumFractionDigits: 0 })}`;

  const formatDate = (value: any) => {
    if (!value) return "-";
    const date = new Date(value);
    if (isNaN(date.getTime())) return String(value);
    return date.toLocaleString("es-CO");
  };

  return (
    <Dialog open={open} onClose={() => setOpen(false)} maxWidth="lg" fullWidth>
      <div className="DialogContainer">
        <div className="ModuleWrapper">
          <div className="boxContainerHeader">
            <div className="boxContainerTitle">
              <h2 className="h2_title">Historial de dinero disponible</h2>
              <span className="h2_subtitle">{user?.user_name || ""}</span>
            </div>
          </div>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: "#a8a8a8", borderBottom: "none" }}>Fecha</TableCell>
                  <TableCell sx={{ color: "#a8a8a8", borderBottom: "none" }}>Asignado</TableCell>
                  <TableCell sx={{ color: "#a8a8a8", borderBottom: "none" }}>Consumido</TableCell>
                  <TableCell sx={{ color: "#a8a8a8", borderBottom: "none" }}>Restante</TableCell>
                  <TableCell sx={{ color: "#a8a8a8", borderBottom: "none" }}>Asignado por</TableCell>
                  <TableCell sx={{ color: "#a8a8a8", borderBottom: "none" }}>Observaciones</TableCell>
                  <TableCell sx={{ color: "#a8a8a8", borderBottom: "none" }}>Estado</TableCell>
                  <TableCell sx={{ color: "#a8a8a8", borderBottom: "none" }} align="right">
                    Consumos
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {list.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <Typography variant="body2" color="text.secondary">
                        Este usuario aún no tiene asignaciones de dinero disponible.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {list.map((item, i) => (
                  <TableRow key={i} hover selected={selectedBudgetId === item.budget_id}>
                    <TableCell>{formatDate(item.created_at)}</TableCell>
                    <TableCell>{currency(item.amount)}</TableCell>
                    <TableCell>{currency(item.consumed)}</TableCell>
                    <TableCell style={{ color: Number(item.available) < 0 ? "#d32f2f" : undefined }}>
                      {currency(item.available)}
                    </TableCell>
                    <TableCell>{item.created_by_name || "-"}</TableCell>
                    <TableCell>{item.comments || "-"}</TableCell>
                    <TableCell>
                      {item.is_active ? (
                        <Chip label="Activo" color="success" size="small" />
                      ) : (
                        <Chip label="Histórico" size="small" />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Button size="small" variant="outlined" onClick={() => loadMovements(item.budget_id)}>
                        Ver
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Movimientos de la asignación seleccionada */}
          {selectedBudgetId && (
            <div style={{ marginTop: 24 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Pedidos que consumieron esta asignación
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: "#a8a8a8", borderBottom: "none" }}>Fecha</TableCell>
                      <TableCell sx={{ color: "#a8a8a8", borderBottom: "none" }}>Pedido</TableCell>
                      <TableCell sx={{ color: "#a8a8a8", borderBottom: "none" }}>Descontado</TableCell>
                      <TableCell sx={{ color: "#a8a8a8", borderBottom: "none" }}>Restante</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {movements.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4}>
                          <Typography variant="body2" color="text.secondary">
                            Sin consumos registrados.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                    {movements.map((m, i) => (
                      <TableRow key={i} hover>
                        <TableCell>{formatDate(m.created_at)}</TableCell>
                        <TableCell>{m.company_order_id || m.order_id || "-"}</TableCell>
                        <TableCell>{currency(m.amount)}</TableCell>
                        <TableCell style={{ color: Number(m.balance_after) < 0 ? "#d32f2f" : undefined }}>
                          {currency(m.balance_after)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          )}

          <div className="dialogButtons">
            <Button onClick={() => setOpen(false)} variant="outlined">
              Cerrar
            </Button>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
